import React, { useState, useEffect, useContext, useMemo } from 'react';
import styles from './ComAllCampaigns.module.css';
import { motion } from 'framer-motion';
import { FaSort, FaSearch, FaSync, FaPlus } from 'react-icons/fa';
import CampaignPreviewModal from '../../Components/Communications/Campaigns/CampaignPreviewModal';
import { Link } from 'react-router-dom'
import CreateCampaignPopup from '../../Components/Communications/Campaigns/CreateCampaignPopup';
import { FiDownload } from 'react-icons/fi';
import AuthContext from '../../store/auth-context';
import ActiveStoreContext from '../../store/ActiveStoreContext';
import { BASE_URL_API } from '../../References/URLs';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router';

const ComAllCampaigns = () => {
    const authCtx = useContext(AuthContext)
    const asCtx = useContext(ActiveStoreContext)
    const activeStore = (() => { try { return (asCtx?.activeStore && asCtx.activeStore.trim() !== "") ? JSON.parse(asCtx.activeStore) : null; } catch { return null; } })();
    const [campaignsData, setCampaignsData] = useState([])

    const nav = useNavigate()
    const [isPopUpOpen, setIsPopUpOpen] = useState(false)
    const campaignCreationHandler = (k) => {
        toast(`Campaign successfully created!`)
        nav(`/communications/campaign/${k}/details`)
    }

    const [searchQuery, setSearchQuery] = useState('');
    const [sortBy, setSortBy] = useState('createdOn');
    const [sortOrder, setSortOrder] = useState('desc');
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [filters, setFilters] = useState({ status: 'all', template: 'all', dateRange: 'all' });

    const getAllCampaigns = async () => {
        setLoading(true)
        try {
            const getAllCampaignsResponse = await fetch(BASE_URL_API + "getAllCampaigns",
                {
                    method: "POST",
                    body: JSON.stringify({
                        token: authCtx.token,
                        storeIdentifier: activeStore?.storeIdentifier
                    })
                });

            if (!getAllCampaignsResponse.ok) {
                throw new Error('Server error while fetching campaigns');
            }

            const getAllCampaignsRespo = await getAllCampaignsResponse.json()
            if (getAllCampaignsRespo?.status === "success") {
                setCampaignsData(getAllCampaignsRespo?.response || [])
            } else if (getAllCampaignsRespo?.message === "tokenExpired") {
                authCtx.logout()
            } else {
                setCampaignsData([])
            }
        } catch (error) {
            console.error(error)
            setCampaignsData([])
        } finally {
            setLoading(false)
        }
    }


    // useEffect(() => {
    //     getAllCampaigns()
    // }, [searchQuery, sortBy, sortOrder, filters, currentPage, rowsPerPage]);

    useEffect(() => {
        getAllCampaigns()
    }, []);

    const [selectedCampaign, setSelectedCampaign] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const normalizedCampaigns = useMemo(() => {
        const toStatus = (campaign) => {
            if (campaign?.hasDeleted === '1' || campaign?.hasDeleted === 1) {
                return 'Deleted';
            }
            if (campaign?.isActive === '1' || campaign?.isActive === 1 || campaign?.status === 'Active') {
                return 'Active';
            }
            if (campaign?.isActive === '0' || campaign?.isActive === 0 || campaign?.status === 'Inactive') {
                return 'Inactive';
            }
            return '';
        };

        return campaignsData.map((campaign, index) => {
            const identifierCandidates = [
                campaign?.campaignIdentifier,
                campaign?.ID,
                campaign?.id,
                campaign?.identifier,
            ];

            const resolvedIdentifier = identifierCandidates.find((value) => typeof value === 'string' && value.trim() !== '')
                || `campaign-${index}`;

            const name = campaign?.campaignName || campaign?.name || 'Untitled Campaign';

            const description = campaign?.campaignDescription || campaign?.description || '';

            const createdOn = campaign?.createdOn
                || campaign?.created_at
                || campaign?.createdDate
                || campaign?.creationDate
                || '';

            const createdBy = campaign?.createdBy || campaign?.created_by || '';

            const templateName = campaign?.templateName || campaign?.template || '';

            const status = toStatus(campaign);

            const type = campaign?.type || campaign?.channel || '—';
            const reach = campaign?.reach ?? '—';
            const clicks = campaign?.clicks ?? '—';
            const budget = campaign?.budget ?? '—';
            const performance = campaign?.performance || '—';

            return {
                id: resolvedIdentifier,
                campaignIdentifier: resolvedIdentifier,
                name,
                description,
                createdOn,
                date: createdOn,
                createdBy,
                templateName,
                status,
                statusKey: status.toLowerCase(),
                templateIdentifier: campaign?.templateIdentifier || '',
                storeIdentifier: campaign?.storeIdentifier || '',
                type,
                reach,
                clicks,
                budget,
                performance,
                raw: campaign,
            };
        });
    }, [campaignsData]);

    const templateOptions = useMemo(() => {
        const unique = new Set();
        normalizedCampaigns.forEach((campaign) => {
            if (campaign.templateName && campaign.templateName.trim() !== '') {
                unique.add(campaign.templateName.trim());
            }
        });
        return Array.from(unique).sort((a, b) => a.localeCompare(b));
    }, [normalizedCampaigns]);

    const processedCampaigns = useMemo(() => {
        let data = [...normalizedCampaigns];

        const normalize = (value) => (value ?? '').toString().toLowerCase().trim();
        const search = normalize(searchQuery.trim());

        if (search) {
            data = data.filter((campaign) => {
                const baseMatch = [
                    normalize(campaign.name),
                    normalize(campaign.description),
                    normalize(campaign.createdOn),
                    normalize(campaign.createdBy),
                    normalize(campaign.templateName),
                    normalize(campaign.campaignIdentifier),
                    normalize(campaign.templateIdentifier),
                    normalize(campaign.storeIdentifier),
                ].some((field) => field.includes(search));

                if (baseMatch) {
                    return true;
                }

                const raw = campaign.raw;
                if (raw && typeof raw === 'object') {
                    return [
                        normalize(raw?.status),
                        normalize(raw?.type),
                    ].some((field) => field.includes(search));
                }

                return false;
            });
        }

        if (filters.status !== 'all') {
            data = data.filter((campaign) => campaign.statusKey === filters.status);
        }

        if (filters.template !== 'all') {
            data = data.filter((campaign) => normalize(campaign.templateName) === normalize(filters.template));
        }

        if (filters.dateRange !== 'all') {
            const now = new Date();
            const boundary = new Date();
            if (filters.dateRange === '7d') {
                boundary.setDate(now.getDate() - 7);
            } else if (filters.dateRange === '30d') {
                boundary.setDate(now.getDate() - 30);
            }

            data = data.filter((campaign) => {
                if (!campaign.createdOn) return false;
                const parsed = new Date(campaign.createdOn.replace(' ', 'T'));
                if (Number.isNaN(parsed.getTime())) return false;
                return parsed >= boundary && parsed <= now;
            });
        }

        const parseDate = (value) => {
            if (!value) return 0;
            const parsed = Date.parse(value.replace(' ', 'T'));
            return Number.isNaN(parsed) ? 0 : parsed;
        };

        data.sort((a, b) => {
            const multiplier = sortOrder === 'asc' ? 1 : -1;

            if (sortBy === 'name') {
                return multiplier * normalize(a.name).localeCompare(normalize(b.name));
            }

            if (sortBy === 'status') {
                const order = { active: 0, inactive: 1, deleted: 2, '': 3 };
                const rankA = order[a.statusKey] ?? 3;
                const rankB = order[b.statusKey] ?? 3;
                return multiplier * (rankA - rankB);
            }

            if (sortBy === 'templateName') {
                return multiplier * normalize(a.templateName).localeCompare(normalize(b.templateName));
            }

            const dateA = parseDate(a.createdOn);
            const dateB = parseDate(b.createdOn);
            return multiplier * (dateA - dateB);
        });

        return data;
    }, [normalizedCampaigns, filters, searchQuery, sortBy, sortOrder]);

    const paginatedData = useMemo(() => (
        processedCampaigns.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage)
    ), [processedCampaigns, currentPage, rowsPerPage]);

    useEffect(() => {
        const totalPages = Math.max(1, Math.ceil(processedCampaigns.length / rowsPerPage));
        if (currentPage > totalPages) {
            setCurrentPage(1);
        }
    }, [processedCampaigns, rowsPerPage, currentPage]);

    const handleOpenPreview = (campaign) => {
        if (!campaign) {
            return;
        }

        const enrichedCampaign = campaign.raw
            ? { ...campaign.raw, ...campaign }
            : campaign;

        setSelectedCampaign(enrichedCampaign);
        setIsModalOpen(true);
    };

    const handleClosePreview = () => {
        setSelectedCampaign(null);
        setIsModalOpen(false);
    };

    const handleSearch = (e) => {
        setSearchQuery(e.target.value);
        setCurrentPage(1);
    };

    const handleSort = (field) => {
        const newSortOrder = sortBy === field && sortOrder === 'asc' ? 'desc' : 'asc';
        setSortBy(field);
        setSortOrder(newSortOrder);
        setCurrentPage(1);
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleRowsPerPageChange = (e) => {
        setRowsPerPage(Number(e.target.value));
        setCurrentPage(1);
    };

    const handleFilterChange = (key, value) => {
        setFilters((prev) => ({ ...prev, [key]: value }));
        setCurrentPage(1);
    };

    const exportToCSV = () => {
        const headers = ['Campaign Name', 'Created On', 'Status', 'Template Name', 'Created By', 'Description'];
        const rows = processedCampaigns.map((campaign) => [
            campaign.name,
            campaign.createdOn,
            campaign.status || '—',
            campaign.templateName || '—',
            campaign.createdBy || '—',
            (campaign.description || '').replace(/\n/g, ' '),
        ]);

        let csvContent = "data:text/csv;charset=utf-8," + [headers, ...rows].map(e => e.join(",")).join("\n");
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", "campaigns.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <motion.div
            className={styles.container}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
        >

            {/* Campaign Preview Modal */}
            <CampaignPreviewModal
                isOpen={isModalOpen}
                onClose={handleClosePreview}
                campaign={selectedCampaign}
            />
            <header className={styles.header}>
                <div>All Campaigns</div>
                <div className={styles.searchFilter}>
                    <div className={styles.searchBar}>
                        <FaSearch className={styles.icon} />
                        <input
                            type="text"
                            placeholder="Search campaigns..."
                            value={searchQuery}
                            onChange={handleSearch}
                        />
                    </div>
                    <div className={styles.filters}>
                        <select value={filters.status} onChange={(e) => handleFilterChange('status', e.target.value)}>
                            <option value="all">All statuses</option>
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                            <option value="deleted">Deleted</option>
                        </select>
                        <select value={filters.template} onChange={(e) => handleFilterChange('template', e.target.value)}>
                            <option value="all">All templates</option>
                            {templateOptions.map((template) => (
                                <option key={template} value={template}>{template}</option>
                            ))}
                        </select>
                        <select value={filters.dateRange} onChange={(e) => handleFilterChange('dateRange', e.target.value)}>
                            <option value="all">All dates</option>
                            <option value="7d">Last 7 Days</option>
                            <option value="30d">Last 30 Days</option>
                        </select>
                        <button className={styles.exportBtn} onClick={exportToCSV}>CSV <FiDownload /></button>
                    </div>
                    <div>
                        {isPopUpOpen && <CreateCampaignPopup isOpen={isPopUpOpen} onClose={() => setIsPopUpOpen(false)} onSuccess={campaignCreationHandler} />}
                        <button className={styles.createNewCampaign} onClick={() => setIsPopUpOpen(true)} >
                            <FaPlus size={18} />
                        </button>
                    </div>
                </div>
            </header>
            {loading ? (
                <div className={styles.loader}>
                    <FaSync className={styles.loaderIcon} />
                    <p>Loading campaigns...</p>
                </div>
            ) : (
                <>
                    <div className={styles.table}>
                        <div className={styles.tableHeader}>
                            <div onClick={() => handleSort('name')}
                                className={styles.campaignName}
                                >
                                Campaign Name <FaSort className={styles.sortIcon} />
                            </div>
                            <div onClick={() => handleSort('createdOn')}
                                className={styles.createdOn}
                            >
                                Date <FaSort className={styles.sortIcon} />
                            </div>
                            <div onClick={() => handleSort('templateName')}
                                className={styles.type}
                            >
                                Template <FaSort className={styles.sortIcon} />
                            </div>
                            {/* <div className={styles.reach} >Created By</div> */}
                            <div onClick={() => handleSort('status')} className={styles.status} >Status <FaSort className={styles.sortIcon} /></div>
                            <div className={styles.action} >Action</div>
                        </div>
                        <div className={styles.tableBody}>
                            {paginatedData.length === 0 ? (
                                <div className={styles.emptyState}>No campaigns match the current filters.</div>
                            ) : (
                                paginatedData.map((campaign) => (
                                    <Link
                                        className={styles.tableRow}
                                        key={campaign.id}
                                        to={`/communications/campaign/${campaign?.campaignIdentifier}/details`}
                                    >
                                        <div className={styles.campaignName}>{campaign.name}</div>
                                        <div className={styles.createdOn} >{campaign.createdOn || '—'}</div>
                                        <div className={styles.type} >{campaign.templateName || '—'}</div>
                                        {/* <div className={styles.reach} >{campaign.createdBy || '—'}</div>  */}
                                        <div className={styles.status} >{campaign.status || '—'}</div>
                                        <div className={styles.action}>
                                            {/* <button
                                                className={styles.previewBtn}
                                                onClick={() => handleOpenPreview(campaign)}
                                            >
                                                Preview
                                            </button> */}
                                            <Link to={`/communications/campaign/${campaign?.campaignIdentifier}/details`}>
                                                View
                                            </Link>
                                        </div>
                                    </Link>
                                ))
                            )}
                        </div>
                    </div>
                    <div className={styles.paginationControls}>
                        <select value={rowsPerPage} onChange={handleRowsPerPageChange}>
                            <option value={5}>5 rows per page</option>
                            <option value={10}>10 rows per page</option>
                            <option value={15}>15 rows per page</option>
                            <option value={50}>50 rows per page</option>
                        </select>
                        <div className={styles.pagination}>
                            {Array.from(
                                { length: Math.max(1, Math.ceil(processedCampaigns.length / rowsPerPage)) },
                                (_, i) => (
                                    <button
                                        key={i}
                                        className={`${styles.pageButton} ${currentPage === i + 1 ? styles.activePage : ''
                                            }`}
                                        onClick={() => handlePageChange(i + 1)}
                                    >
                                        {i + 1}
                                    </button>
                                )
                            )}
                        </div>
                    </div>
                </>
            )}
        </motion.div>
    );
};

export default ComAllCampaigns;
